/**
 * Morgenreport — die Ansicht als DATEN, eine Quelle für Admin-Protokoll und In-App-Seite. (19.10.2026)
 *
 * Martin 09:07: „Alle Einzelheiten“ soll in der App bleiben. Beide Oberflächen (server/admin-morgenreport-seite.ts als HTML,
 * frontend/lib/morgenreport-seite.ts als DOM) zeigen dieselben Bereiche mit denselben Regeln: Ampel mit Wort, Kennzahlen,
 * Listen. Die Regeln stehen NUR hier; die Oberflächen setzen die Daten nur noch in Form.
 *
 * ⚠️ Alle Texte sind Klartext (nie HTML). Wer sie ausgibt, schützt sie selbst (Admin: schuetze, App: textContent).
 */
import type { MorgenInhalt, TeamZahlen, TicketKurz } from "./morgenreport.js";

export type AnsichtTon = "ok" | "achtung" | "problem" | "neutral";

export interface AnsichtKennzahl {
  name: string;
  wert: string;
  /** Kleiner Satz darunter („keine Daten“). */
  hinweis?: string;
}

export interface AnsichtZeile {
  vor?: string;
  text: string;
  unter?: string;
}

export interface AnsichtListe {
  /** `aufklapper`: eingeklappt mit Titel; `offen`: steht da. */
  art: "aufklapper" | "offen";
  titel?: string;
  zeilen: AnsichtZeile[];
  /** „und 3 weitere“ unter einer gekürzten Liste. */
  rest?: string;
  /** Wie viele Zeilen zuerst stehen (Rest hinter „Mehr“). */
  zeige?: number;
  /** Statt einer leeren Liste: ein ruhiger Satz. */
  leer?: { satz: string; zusatz: string };
}

export interface AnsichtBereich {
  id: string;
  titel: string;
  ton: AnsichtTon;
  wort: string;
  kennzahlen: AnsichtKennzahl[];
  dreispaltig?: boolean;
  listen: AnsichtListe[];
  /** Name links, Wert rechts (Bereich „Neues“). */
  paare?: Array<{ name: string; wert: string }>;
}

export interface AnsichtSignal {
  ton: AnsichtTon;
  text: string;
  /** Woher das Signal kommt — die In-App-Seite zeigt „fragen“ im Abschnitt „Wartet auf dich“ statt oben. */
  art: "ausfall" | "fehler" | "fragen";
}

const KEINE_DATEN = "keine Daten";

/** Ampel Vespera nach Fehlern in 24 h: 0 grün, 1 bis 4 gelb, ab 5 rot. */
export const FEHLER_ROT = 5;

export const zeitText = (ms: number): string =>
  new Intl.DateTimeFormat("de-AT", { timeZone: "Europe/Vienna", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(ms));

/** Nur die Uhrzeit („08:10“), Wiener Zeit — das Datum steht im Kopf der Seite genau einmal. */
export const uhrText = (ms: number): string =>
  new Intl.DateTimeFormat("de-AT", { timeZone: "Europe/Vienna", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(ms));

/** „1 Frage wartet“ / „3 Fragen warten“. */
export const fragenWartenText = (n: number): string => (n === 1 ? "1 Frage wartet" : `${n} Fragen warten`);

/** „19.10.“ aus „2026-10-19“ — die Kurzform für die Tage-Leiste. */
export const tagKurz = (tag: string): string => {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(tag);
  return m ? `${m[2]}.${m[1]}.` : tag;
};

const minutenText = (s: number): string => {
  const min = Math.max(0, Math.round(s / 60));
  return min < 120 ? `vor ${min} Min.` : `vor ${Math.round(min / 60)} Std.`;
};

/** Eine Kennzahl; ohne Zahl steht ein Strich und darunter „keine Daten“. */
const zahl = (name: string, n: number | null): AnsichtKennzahl => (n === null ? { name, wert: "–", hinweis: KEINE_DATEN } : { name, wert: String(n) });

const KEINE: { ton: AnsichtTon; wort: string } = { ton: "neutral", wort: "Keine Daten" };
const AUS: { ton: AnsichtTon; wort: string } = { ton: "problem", wort: "Quelle ausgefallen" };
const OK: { ton: AnsichtTon; wort: string } = { ton: "ok", wort: "In Ordnung" };

/** Welche Quellen (Namen aus server/morgenreport.ts) zu welchem Bereich gehören. */
const QUELLEN = {
  vespera: ["vespera"],
  schmied: ["commits", "version", "tickets-erledigt"],
  team: ["team"],
  neues: ["claude-code"],
  wuensche: ["vorschlaege", "rueckmeldungen"],
  ziel: ["in-arbeit", "fragen"],
} as const;

const faelltAus = (i: MorgenInhalt, b: keyof typeof QUELLEN): boolean => QUELLEN[b].some((q) => i.ausgefallen.includes(q));

const ticketZeilen = (ts: readonly TicketKurz[]): AnsichtZeile[] => ts.map((t) => ({ vor: `Ticket ${Math.trunc(t.nr)}`, text: t.titel }));

function vespera(i: MorgenInhalt): AnsichtBereich {
  const v = i.vespera;
  const alle = [v.zuege24h, v.gespraeche24h, v.werkzeugLaeufe24h, v.fehler24h];
  const s = faelltAus(i, "vespera")
    ? AUS
    : alle.every((x) => x === null)
      ? KEINE
      : (v.fehler24h ?? 0) >= FEHLER_ROT
        ? { ton: "problem" as const, wort: `${v.fehler24h} Fehler` }
        : (v.fehler24h ?? 0) > 0
          ? { ton: "achtung" as const, wort: `${v.fehler24h} Fehler` }
          : { ton: "ok" as const, wort: "Keine Fehler" };
  return {
    id: "m-vespera",
    titel: "Vespera",
    ...s,
    kennzahlen: [zahl("Züge (24 h)", v.zuege24h), zahl("Gespräche (24 h)", v.gespraeche24h), zahl("Werkzeugläufe (24 h)", v.werkzeugLaeufe24h), zahl("Fehler (24 h)", v.fehler24h)],
    listen: [],
  };
}

function schmied(i: MorgenInhalt): AnsichtBereich {
  const s = i.schmied;
  const anzahl = s.commits.length + s.commitsGekuerzt;
  const st = faelltAus(i, "schmied") ? AUS : s.version === null && anzahl === 0 && s.erledigt.length === 0 ? KEINE : OK;
  const listen: AnsichtListe[] = [];
  if (s.commits.length) {
    listen.push({
      art: "aufklapper",
      titel: `Änderungen im Hauptpfad (${anzahl})`,
      zeilen: s.commits.map((c) => ({ text: c })),
      rest: s.commitsGekuerzt > 0 ? `und ${s.commitsGekuerzt} weitere` : undefined,
    });
  }
  if (s.erledigt.length) listen.push({ art: "aufklapper", titel: `Erledigte Tickets (${s.erledigt.length})`, zeilen: ticketZeilen(s.erledigt) });
  return {
    id: "m-schmied",
    titel: "Schmied",
    ...st,
    kennzahlen: [
      { name: "Server-Version", wert: s.version ?? "–", hinweis: s.version === null ? KEINE_DATEN : undefined },
      { name: "Änderungen im Hauptpfad", wert: String(anzahl) },
      { name: "Erledigte Tickets", wert: String(s.erledigt.length) },
    ],
    dreispaltig: true,
    listen,
  };
}

function team(i: MorgenInhalt, id: string, name: string, t: TeamZahlen): AnsichtBereich {
  const leerTeam = t.beitraege24h === null && t.letzterBeitragMs === null && t.hoertMit === null && t.zuletztGesehenS === null;
  const st = faelltAus(i, "team")
    ? AUS
    : leerTeam
      ? KEINE
      : (t.beitraege24h ?? 0) > 0
        ? { ton: "ok" as const, wort: "Aktiv" } // Beiträge sind der Beleg; „zuletzt gesehen“ gibt es nur seit dem Server-Start und darf sie nie widerlegen
        : t.zuletztGesehenS !== null
          ? { ton: "ok" as const, wort: t.hoertMit ? "Hört mit" : "Erreichbar" }
          : { ton: t.hoertMit === true ? ("achtung" as const) : ("neutral" as const), wort: "Kein Lebenszeichen" }; // eingeschaltet (hört mit) und still: Warnung; sonst schlicht aus oder neu gestartet
  return {
    id,
    titel: name,
    ...st,
    kennzahlen: [
      zahl("Beiträge (24 h)", t.beitraege24h),
      { name: "Letzter Beitrag", wert: t.letzterBeitragMs ? zeitText(t.letzterBeitragMs) : "keiner" },
      { name: "Hört mit", wert: t.hoertMit === null ? "–" : t.hoertMit ? "ja" : "nein", hinweis: t.hoertMit === null ? KEINE_DATEN : undefined },
      { name: "Zuletzt gesehen", wert: t.zuletztGesehenS === null ? "nie" : minutenText(t.zuletztGesehenS), hinweis: t.zuletztGesehenS === null ? "seit dem Start" : undefined },
    ],
    listen: [],
  };
}

function neues(i: MorgenInhalt): AnsichtBereich {
  const st = faelltAus(i, "neues") ? AUS : i.neues.claudeCode === "unbekannt" && i.neues.server === null ? KEINE : OK;
  return {
    id: "m-neues",
    titel: "Neues",
    ...st,
    kennzahlen: [],
    listen: [],
    paare: [
      { name: "Claude Code", wert: i.neues.claudeCode },
      { name: "Server", wert: i.neues.server ?? "unbekannt" },
    ],
  };
}

function wuensche(i: MorgenInhalt): AnsichtBereich {
  const w = i.wuensche;
  const st = faelltAus(i, "wuensche") ? AUS : w.rueckmeldungen === null && w.vorschlaege.length === 0 ? KEINE : OK;
  return {
    id: "m-wuensche",
    titel: "Wünsche und Beschwerden",
    ...st,
    kennzahlen: [zahl("Vorschläge", w.vorschlaege.length), zahl("Rückmeldungen (nur Anzahl)", w.rueckmeldungen)],
    listen: [
      {
        art: "offen",
        zeilen: w.vorschlaege.map((x) => ({ text: x.titel, unter: x.marke || undefined })),
        zeige: 4,
        leer: { satz: "Keine Vorschläge.", zusatz: " Die Selbstverbesserung hat nichts offen." },
      },
    ],
  };
}

function ziel(i: MorgenInhalt): AnsichtBereich {
  const z = i.ziel;
  const st = faelltAus(i, "ziel")
    ? AUS
    : z.wartendeFragen === null && z.inArbeit.length === 0
      ? KEINE
      : (z.wartendeFragen ?? 0) > 0
        ? { ton: "achtung" as const, wort: fragenWartenText(z.wartendeFragen ?? 0) }
        : { ton: "ok" as const, wort: "Nichts wartet" };
  return {
    id: "m-ziel",
    titel: "Ziel des Tages",
    ...st,
    kennzahlen: [zahl("In Arbeit", z.inArbeit.length), zahl("Wartende Fragen an dich", z.wartendeFragen)],
    listen: [{ art: "offen", zeilen: ticketZeilen(z.inArbeit), leer: { satz: "Nichts in Arbeit.", zusatz: "" } }],
  };
}

/** Alle Bereiche in der Reihenfolge der Seite. */
export function ansichtBereiche(i: MorgenInhalt): AnsichtBereich[] {
  return [vespera(i), schmied(i), team(i, "m-zimmermann", "Zimmermann", i.team.zimmermann), team(i, "m-hueter", "Hüter", i.team.hueter), neues(i), wuensche(i), ziel(i)];
}

/** Der Stand oben: die Signale, das Schlimmste zuerst sortiert die Oberfläche (Admin: zustandVon). */
export function ansichtSignale(i: MorgenInhalt): AnsichtSignal[] {
  const s: AnsichtSignal[] = [];
  if (i.ausgefallen.length) s.push({ ton: "problem", art: "ausfall", text: `Nicht lesbar waren: ${i.ausgefallen.join(", ")}.` });
  if ((i.vespera.fehler24h ?? 0) > 0) s.push({ ton: (i.vespera.fehler24h ?? 0) >= FEHLER_ROT ? "problem" : "achtung", art: "fehler", text: `Vespera meldet ${i.vespera.fehler24h} Fehler in 24 Stunden.` });
  if ((i.ziel.wartendeFragen ?? 0) > 0) s.push({ ton: "achtung", art: "fragen", text: `${fragenWartenText(i.ziel.wartendeFragen ?? 0)} auf dich.` });
  return s;
}

/** Der Satz für „alles ruhig“. */
export const ANSICHT_RUHIG = "Alle Bereiche gelesen, nichts Auffälliges.";

export interface AnsichtKachel {
  id: string;
  titel: string;
  ton: AnsichtTon;
  wort: string;
  /** Die EINE Zahl der Kachel („27“, ohne Daten „–“) … */
  zahl: string;
  /** … und was sie bedeutet, als Satzteil direkt daneben („Züge in 5 Gesprächen“) — nie eine nackte Zahl (Martin 20.10.). */
  bedeutung: string;
  /** Ein, zwei leise Angaben darunter („12 Werkzeugläufe“, „zuletzt um 06:40“). */
  details: string[];
}

const TAG_MS = 24 * 60 * 60_000;

/** „1 Zug“ / „3 Züge“. */
const mal = (n: number, eins: string, viele: string): string => (n === 1 ? eins : viele);

/**
 * Die Kacheln „Über Nacht“: je Bereich eine Zahl MIT Bedeutung und ein, zwei Angaben darunter. Ton und Wort wie im Bereich.
 * Nur Zahlen und Zeiten aus dem Report (nichts aus Gesprächen); ohne Daten steht „–“ und „keine Daten“.
 */
export function ansichtKacheln(i: MorgenInhalt): AnsichtKachel[] {
  const [vb, sb, zb, hb] = ansichtBereiche(i);
  if (!vb || !sb || !zb || !hb) return [];
  // Quelle ausgefallen: keine Zahl, keine Angaben (sonst stünde „0 Änderungen“ neben „Quelle ausgefallen“). Ohne Zahl auch keine Angaben.
  const kachel = (b: AnsichtBereich, roh: number | null, bedeutung: (n: number) => string, details: Array<string | null>): AnsichtKachel => {
    const zahl = b.wort === AUS.wort ? null : roh;
    return {
      id: `k-${b.id.slice(2)}`,
      titel: b.titel,
      ton: b.ton,
      wort: b.wort,
      zahl: zahl === null ? "–" : String(zahl),
      bedeutung: zahl === null ? KEINE_DATEN : bedeutung(zahl),
      details: zahl === null ? [] : details.filter((d): d is string => !!d),
    };
  };
  const v = i.vespera;
  const s = i.schmied;
  // Nur ein Beitrag aus den letzten 24 Stunden steht als bloße Uhrzeit; ein älterer (oder bei 0 Beiträgen) mit Datum —
  // sonst widerspräche „zuletzt um 10:53 Uhr“ dem Abschnitt „letzte 24 Stunden“ und der Pille „Kein Lebenszeichen“.
  const letzterText = (t: TeamZahlen): string | null => {
    if (t.letzterBeitragMs) {
      const frisch = (t.beitraege24h ?? 0) > 0 && i.erzeugtMs - t.letzterBeitragMs <= TAG_MS;
      return frisch ? `zuletzt um ${uhrText(t.letzterBeitragMs)} Uhr` : `letzter Beitrag ${zeitText(t.letzterBeitragMs)} Uhr`;
    }
    return t.beitraege24h === null ? null : "kein Beitrag in 24 Stunden";
  };
  const team = (b: AnsichtBereich, t: TeamZahlen): AnsichtKachel =>
    kachel(b, t.beitraege24h, (n) => `${mal(n, "Beitrag", "Beiträge")} im Chat`, [
      letzterText(t),
      t.hoertMit === null ? null : t.hoertMit ? "hört mit" : "hört nicht mit",
    ]);
  return [
    kachel(vb, v.zuege24h, (n) => `${mal(n, "Zug", "Züge")}${v.gespraeche24h === null ? "" : ` in ${v.gespraeche24h} ${mal(v.gespraeche24h, "Gespräch", "Gesprächen")}`}`, [
      v.werkzeugLaeufe24h === null ? null : `${v.werkzeugLaeufe24h} ${mal(v.werkzeugLaeufe24h, "Werkzeuglauf", "Werkzeugläufe")}`,
      "in 24 Stunden",
    ]),
    kachel(sb, s.commits.length + s.commitsGekuerzt, (n) => `${mal(n, "Änderung", "Änderungen")} im Hauptpfad`, [
      `${s.erledigt.length} ${mal(s.erledigt.length, "Ticket", "Tickets")} erledigt`,
      s.version ? `Server ${s.version}` : null,
    ]),
    team(zb, i.team.zimmermann),
    team(hb, i.team.hueter),
  ];
}

/** Eine Zeile im Abschnitt „Wartet auf dich“: Ton (Form + Wort an der Marke), Satz, darunter leise Titel. */
export interface AnsichtWartet {
  id: string;
  ton: AnsichtTon;
  text: string;
  zeilen: string[];
}

/** So viele Titel stehen unter „offene Vorschläge“; der Rest steht in „Alle Einzelheiten“. */
export const WARTET_TITEL = 3;

/** „Wartet auf dich“: wartende Fragen und offene Vorschläge — dieselben Zahlen wie in den Bereichen Ziel und Wünsche. */
export function ansichtWartet(i: MorgenInhalt): AnsichtWartet[] {
  const f = i.ziel.wartendeFragen;
  const vs = i.wuensche.vorschlaege;
  // Eine ausgefallene Quelle liefert eine leere Liste (server/morgenreport.ts) — das ist „keine Daten“, nie ein grünes „keine“.
  const fragenAus = i.ausgefallen.includes("fragen");
  const vorschlaegeAus = i.ausgefallen.includes("vorschlaege");
  const fragen: AnsichtWartet =
    f === null || fragenAus
      ? { id: "w-fragen", ton: "neutral", text: "Wartende Fragen: keine Daten.", zeilen: [] }
      : f > 0
        ? { id: "w-fragen", ton: "achtung", text: `${fragenWartenText(f)} auf dich.`, zeilen: [] }
        : { id: "w-fragen", ton: "ok", text: "Keine Frage wartet auf dich.", zeilen: [] };
  const vorschlaege: AnsichtWartet = vorschlaegeAus
    ? { id: "w-vorschlaege", ton: "neutral", text: "Offene Vorschläge: keine Daten.", zeilen: [] }
    : vs.length
    ? {
        id: "w-vorschlaege",
        ton: "neutral",
        text: `${vs.length} ${mal(vs.length, "offener Vorschlag", "offene Vorschläge")} der Selbstverbesserung.`,
        zeilen: [...vs.slice(0, WARTET_TITEL).map((x) => x.titel), ...(vs.length > WARTET_TITEL ? [`und ${vs.length - WARTET_TITEL} weitere in den Einzelheiten`] : [])],
      }
    : { id: "w-vorschlaege", ton: "ok", text: "Keine offenen Vorschläge.", zeilen: [] };
  return [fragen, vorschlaege];
}

/** Der Wochentag zum Tag („Montag“ aus „2026-10-19“); unbekannte Form: leer. Rein, ohne Zeitzonen-Falle (Mittag UTC). */
export function wochentag(tag: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(tag);
  if (!m) return "";
  return new Intl.DateTimeFormat("de-AT", { weekday: "long", timeZone: "UTC" }).format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12)));
}

/** Der Text, den „Ziel mit Schmied besprechen“ ins Eingabefeld legt: nur Ticketnummern und -titel aus dem Report, Martin sendet selbst. */
export function zielBesprechText(i: MorgenInhalt): string {
  const z = i.ziel.inArbeit.slice(0, 3).map((t) => `Ticket ${Math.trunc(t.nr)} (${t.titel})`);
  return z.length ? `Schmied, lass uns das Ziel des Tages besprechen: ${z.join(", ")}. Was ist dein Stand und was ist heute am wichtigsten?` : "Schmied, lass uns das Ziel des Tages besprechen. Was ist heute am wichtigsten?";
}
